import { $createBoundTask, $scopedTask } from "@captions-web/dev-logger";
import { nanoid } from "nanoid";

import { AssetUploadEvents } from "~/lib/analytics/defs/AssetUploadEvents";

import {
  BackendAssetType,
  createAssetMutation,
  createFileUploadMutation,
  getUploadedFileInfoQuery,
  uploadFileChunkMutation,
} from "./network";

const DEFAULT_HTTP2_CHUNK_SIZE = 256 * 1024 * 1000; // 256mb per chunk

interface UploadFileToAssetsOptions {
  file: File;
  origin: string;
  tracingId?: string;
  signal?: AbortSignal;

  onProgress?: (progress: number) => void;
}

export async function uploadFileToAssets({
  file,
  origin,
  tracingId = nanoid(),
  signal,
  onProgress,
}: UploadFileToAssetsOptions) {
  const { fileId } = await $scopedTask(
    "create-file",
    () => createFileUploadMutation.trigger({ params: getFileUploadParams(file), signal }),
    {
      getMetadata: () => ({
        tracing_id: tracingId,
        origin,
      }),
      trackEvent: AssetUploadEvents.status.track,
    }
  );

  const $task = $createBoundTask({
    getMetadata: () => ({
      tracing_id: tracingId,
      origin,
      file_id: fileId,
    }),
    trackEvent: AssetUploadEvents.status.track,
  });

  await $task("upload-all-chunks", () =>
    uploadAllChunks({
      fileId,
      file,
      lastByteUploaded: 0,
      filename: file.name,
      origin,
      signal,
      onProgress(fileUploadProgress) {
        console.log("fileUploadProgress", fileUploadProgress);
        // TODO: update overall progress
      },
    })
  );

  const fileResponse = await $task(
    "get-file-info",
    () =>
      getUploadedFileInfoQuery.beginPolling({
        pathParams: { fileId },
        params: { gcsUri: true },
        signal,
      }).promise
  );

  const { id } = await $task("create-asset", () =>
    createAssetMutation.trigger({
      params: {
        uri: fileResponse.gcsUri,
        type: fileTypeToAssetType(fileResponse.type),
      },
      signal,
    })
  );

  return { assetId: id, url: fileResponse.url };
}

interface UploadChunkOptions extends UploadFileToAssetsOptions {
  fileId: string;
  lastByteUploaded: number;
  filename: string;
}

async function uploadAllChunks(options: UploadChunkOptions) {
  let startByteIndex = options.lastByteUploaded;
  let response = await uploadNextSingleChunk(options);

  while (response.lastByte + 1 < options.file.size) {
    startByteIndex = response.lastByte + 1;
    response = await uploadNextSingleChunk({ ...options, lastByteUploaded: startByteIndex });
    options.onProgress?.(startByteIndex / options.file.size);
  }
}

async function uploadNextSingleChunk({
  fileId,
  file,
  lastByteUploaded,
  filename,
}: UploadChunkOptions) {
  const startByteIndex = lastByteUploaded;
  const endByteIndex = getEndByteIndex(file, startByteIndex);
  const contentRange = getContentRange(file, startByteIndex, endByteIndex);
  const chunk = getChunk(file, startByteIndex, endByteIndex);

  const formData = new FormData();
  formData.append("fileId", fileId);
  formData.append("chunk", chunk, filename);

  return uploadFileChunkMutation.trigger({
    params: formData,
    pathParams: { fileId },
    headers: {
      "Content-Range": contentRange,
    },
  });
}

function getFileUploadParams(file: File) {
  const matchExtension = /^.+\.(.+)$/.exec(file.name);
  if (!matchExtension) {
    throw new Error("Filename without extension");
  }

  return {
    filename: file.name,
    extension: matchExtension[1],
    mimeType: file.type,
    size: file.size,
  };
}

function getEndByteIndex(file: File, startByteIndex: number) {
  // minus 1 to get the endByteIndex
  return Math.min(startByteIndex + DEFAULT_HTTP2_CHUNK_SIZE - 1, file.size - 1);
}

function getContentRange(file: File, startByteIndex: number, endByteIndex: number) {
  return `bytes=${startByteIndex}-${endByteIndex}/${file.size}`;
}

function getChunk(file: File, startByteIndex: number, endByteIndex: number) {
  // indexes are zero based, and slice end is exclusive, so we add 1 to the endByteIndex
  return file.slice(startByteIndex, endByteIndex + 1, file.type);
}

function fileTypeToAssetType(fileType: string) {
  if (fileType === "THUMBNAIL") {
    return "image";
  }

  return fileType.toLowerCase() as BackendAssetType;
}
